import type { ReactNode } from 'react';
import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router-dom';
import { can, type Capability } from '@cercle/shared';
import { useApp } from './shared/app-context';
import { AppShell } from './shared/layout/AppShell';
import { Skeleton } from './shared/ui';
import { LoginScreen } from './features/auth/LoginScreen';
import { MagicLinkCallback } from './features/auth/MagicLinkCallback';
import { InviteScreen } from './features/auth/InviteScreen';
import { SessionScreen } from './features/session/SessionScreen';
import { RecapScreen, SessionsListScreen } from './features/session/RecapScreen';
import { CaveScreen } from './features/cave/CaveScreen';
import { RumScreen } from './features/cave/RumScreen';
import { NewRumScreen } from './features/cave/NewRumScreen';
import { EditRumScreen } from './features/cave/EditRumScreen';
import { TastingScreen } from './features/tasting/TastingScreen';
import { NoteRedirect } from './features/tasting/NoteRedirect';
import { BlindScreen } from './features/blind/BlindScreen';
import { RevealScreen } from './features/blind/RevealScreen';
import { AromaWheelScreen } from './features/aromas/AromaWheelScreen';
import { ProfileScreen } from './features/profile/ProfileScreen';
import { AdminMembersScreen } from './features/admin/AdminMembersScreen';
import { AdminSessionScreen } from './features/admin/AdminSessionScreen';
import { AdminSessionsListScreen } from './features/admin/AdminSessionsListScreen';
import { AdminReferenceScreen } from './features/admin/AdminReferenceScreen';

/**
 * Le cercle est fermé : aucune route applicative n'est accessible sans session, et il n'y
 * a aucune page publique à indexer.
 */
function RequireAuth(): ReactNode {
  const { member, sessionChecked } = useApp();
  const location = useLocation();

  // Tant que la reprise de session est en cours, on n'affiche ni l'application ni la page
  // de connexion : rediriger trop tôt éjecterait un membre pourtant connecté.
  if (!sessionChecked) return <Skeleton lines={4} />;
  if (!member) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <Outlet />;
}

/**
 * Garde une branche du routeur derrière une capacité, jamais derrière un rôle.
 *
 * `member?.role !== 'ADMIN'` fermait la régie au maître de chai sans que rien ne le dise.
 * En nommant la capacité, la route reste juste quand la table des droits change.
 */
function RequireCapability({ capability }: { capability: Capability }): ReactNode {
  const { member } = useApp();
  if (!member || !can(member.role, capability)) return <Navigate to="/" replace />;
  return <Outlet />;
}

function RedirectIfAuthenticated({ children }: { children: ReactNode }): ReactNode {
  const { member, sessionChecked } = useApp();
  if (!sessionChecked) return <Skeleton lines={4} />;
  if (member) return <Navigate to="/" replace />;
  return children;
}

export const router = createBrowserRouter([
  {
    path: '/login',
    element: (
      <RedirectIfAuthenticated>
        <LoginScreen />
      </RedirectIfAuthenticated>
    ),
  },
  { path: '/login/callback', element: <MagicLinkCallback /> },
  { path: '/invite', element: <InviteScreen /> },
  { path: '/invite/:token', element: <InviteScreen /> },

  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          { path: '/', element: <SessionScreen /> },
          { path: '/cave', element: <CaveScreen /> },
          { path: '/rhums/nouveau', element: <NewRumScreen /> },
          { path: '/rhums/:id', element: <RumScreen /> },
          // Pas de `RequireCapability` : le droit de corriger dépend de la fiche et non du
          // seul rôle — un bouilleur complète celle qu'il a saisie. C'est l'écran qui
          // tranche, une fois la fiche lue.
          { path: '/rhums/:id/modifier', element: <EditRumScreen /> },
          { path: '/rhums/:id/noter', element: <TastingScreen /> },
          { path: '/noter', element: <NoteRedirect /> },
          { path: '/classements', element: <SessionsListScreen /> },
          { path: '/seances/:id/recap', element: <RecapScreen /> },
          { path: '/seances/:id/aveugle', element: <BlindScreen /> },
          { path: '/seances/:id/revelation', element: <RevealScreen /> },
          { path: '/aromes', element: <AromaWheelScreen /> },
          { path: '/profil', element: <ProfileScreen /> },

          {
            element: <RequireCapability capability="member:manage" />,
            children: [{ path: '/regie/membres', element: <AdminMembersScreen /> }],
          },
          {
            element: <RequireCapability capability="reference:manage" />,
            children: [{ path: '/regie/referentiel', element: <AdminReferenceScreen /> }],
          },
          {
            element: <RequireCapability capability="session:manage" />,
            children: [
              { path: '/regie/seances', element: <AdminSessionsListScreen /> },
              { path: '/regie/seance', element: <AdminSessionScreen /> },
              { path: '/regie/seance/:id', element: <AdminSessionScreen /> },
            ],
          },

          { path: '*', element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
]);
